import React, { useState, useEffect } from 'react';
import { Button, Spin, Image, Row, Col } from 'antd';
import { FaCube } from 'react-icons/fa';
import { LuScan } from 'react-icons/lu';
import { EnvironmentOutlined } from '@ant-design/icons';
import { getClientType } from '../../../services/clientConfig';
import { fetchClientBlobUrl } from '../../../services/clientDataService';
import '../styles/ply3DTooltip.css';

function Ply3DTooltip({ location, onView3D, visible, position, onClose }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [streetName, setStreetName] = useState("");
  const [loadingStreet, setLoadingStreet] = useState(false);

  // Load preview image via authenticated fetch (blob URLs are cached by clientDataService)
  useEffect(() => {
    if (!visible) return;
    const clientType = getClientType();
    const plyName = location.name.replace('.ply', '');
    fetchClientBlobUrl(clientType, `signal_fix/previews/${plyName}.png`)
      .then(url => {
        setPreviewImage(url);
        setImageLoading(false);
      })
      .catch(() => {
        setImageError(true);
        setImageLoading(false);
      });
  }, [visible, location.name]);

  // Fetch street name when tooltip becomes visible
  useEffect(() => {
    if (!visible) return;

    const fetchStreet = async () => {
      setLoadingStreet(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.lat}&lon=${location.lng}`
        );
        const data = await response.json();
        const roadName =
          data.address?.road ||
          data.address?.pedestrian ||
          data.display_name ||
          "Unknown location";
        setStreetName(roadName);
      } catch (error) {
        console.error("Error during reverse geocoding:", error);
        setStreetName("Unknown location");
      } finally {
        setLoadingStreet(false);
      }
    };

    fetchStreet();
  }, [visible, location.lat, location.lng]);

  if (!visible) return null;

  return (
    <div
      className="ply-3d-tooltip"
      style={{
        position: 'absolute',
        left: `${position.x + 15}px`,
        top: `${position.y - 10}px`,
        zIndex: 9000, // Below image preview modal
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="ply-3d-tooltip-content">
        <button
          className="ply-3d-tooltip-close"
          onClick={(e) => {
            e.stopPropagation();
            if (onClose) onClose();
          }}
          aria-label="Close"
        >
          ×
        </button>

        <div className="ply-3d-popup">
          <h3>
            <b>3D Point Cloud</b>
          </h3>

          <div className="ply-3d-image-container">
            {!imageError ? (
              <>
                <img
                  src={previewImage}
                  alt={`Preview of ${location.name}`}
                  className="ply-3d-image"
                  onLoad={() => setImageLoading(false)}
                  onError={() => {
                    setImageError(true);
                    setImageLoading(false);
                  }}
                  style={{ opacity: imageLoading ? 0 : 1 }}
                />
                {imageLoading && (
                  <div className="ply-3d-image-loading">
                    <Spin size="large" />
                  </div>
                )}
                <button
                  type="button"
                  className="ply-3d-preview-button"
                  onClick={() => setPreviewVisible(true)}
                  disabled={imageLoading}
                >
                  <LuScan size={18} />
                </button>
                <Image
                  src={previewImage}
                  alt={`Preview of ${location.name}`}
                  preview={{
                    visible: previewVisible,
                    onVisibleChange: (visible) => setPreviewVisible(visible),
                    zIndex: 9600, // Explicit z-index for the preview
                    maskClassName: 'ply-image-preview-mask',
                    wrapClassName: 'ply-image-preview-wrap'
                  }}
                  style={{ display: "none" }}
                />
              </>
            ) : (
              <div className="ply-3d-placeholder">
                <FaCube size={40} style={{ color: '#A1E430' }} />
                <p>3D Preview</p>
              </div>
            )}
          </div>

          <Row
            justify={"start"}
            align="middle"
            gutter={8}
            style={{ margin: "18px 0" }}
          >
            <Col>
              <EnvironmentOutlined style={{ color: "#A1E430" }} />
            </Col>
            <Col>
              <span>Indirizzo</span>
              <br />
              <b>{loadingStreet ? <Spin size="small" /> : streetName}</b>
            </Col>
          </Row>

          <Row
            justify={"start"}
            align="middle"
            gutter={8}
            style={{ margin: "18px 0" }}
          >
            <Col>
              <EnvironmentOutlined style={{ color: "#A1E430" }} />
            </Col>
            <Col>
              <span>Coordinate</span>
              <br />
              <b>
                {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
              </b>
            </Col>
          </Row>

          <Row
            justify={"start"}
            align="middle"
            gutter={8}
            style={{ margin: "18px 0" }}
          >
            <Col>
              <FaCube style={{ color: "#A1E430" }} />
            </Col>
            <Col>
              <span>File Point Cloud</span>
              <br />
              <b>{location.name}</b>
            </Col>
          </Row>
        </div>

        <Button
          color="green"
          style={{
            width: "100%",
            borderRadius: "25px",
            backgroundColor: "var(--primary-color)",
            color: "white",
            marginTop: "12px"
          }}
          onClick={() => onView3D(location)}
        >
          <FaCube />
          Visualizza in 3D
        </Button>
      </div>
    </div>
  );
}

export default Ply3DTooltip;